import { useQuery } from "@tanstack/react-query";
import { NavLink, useLocation } from "react-router-dom";

import { useCommandPalette } from "../../lib/command-palette";
import { crmApi } from "../../lib/crm-api";
import { clustersOf } from "../../lib/duplicate-clusters";
import { queryKeys } from "../../lib/queryKeys";
import { NotificationBell } from "../NotificationBell";
import { cn } from "../ui/cn";

/**
 * CRM navigation.
 *
 * Short on purpose. The CRM has one screen that matters -- a company -- and the
 * rest of this list exists to get you to one: search, the list, and the queue
 * of records that are not yet properly filed.
 *
 * Deliberately the same shape as the Helpdesk's `ViewSidebar` rather than
 * something
 * new. Somebody switching between the two apps twenty times a day should not
 * have to re-learn where things are.
 */
export function CrmSidebar({ connected }: { connected: boolean }) {
  /*
   * The platform's box, not the CRM's own.
   *
   * This button used to open a company-only overlay bound to a ⌘K the CRM
   * registered for itself -- which meant the shortcut existed in one of the
   * four products and answered with one of the four kinds of thing. Both are
   * now `AppFrame`'s, so the button is a second way into the same dialog
   * rather than a different dialog wearing the same key.
   */
  const search = useCommandPalette();

  /*
   * Counted in the sidebar because it is work somebody has to do, and a queue
   * you have to click into to discover is empty is a queue nobody checks.
   */
  const duplicates = useQuery({
    queryKey: queryKeys.crmDuplicates("open"),
    queryFn: () => crmApi.duplicates("open"),
    staleTime: 60_000,
  });

  /* Counted in clusters, not flags: four unfiled depots on one domain are six
     flags and one thing to look at, and a badge that said six sent people to a
     page showing one card. */
  const clusters = clustersOf(duplicates.data?.duplicates ?? []);

  return (
    <nav className="flex w-60 shrink-0 flex-col overflow-y-auto border-r border-line bg-app pb-3">
      <div className="flex items-center gap-2 px-4 py-4">
        <span className="text-sm font-semibold text-content">CRM</span>
        <span
          title={connected ? "Live updates connected" : "Reconnecting…"}
          className={cn(
            "size-2 shrink-0 rounded-full",
            connected ? "bg-sla-ok" : "bg-sla-warning",
          )}
        />

        {/*
          The same bell as the Helpdesk's, reading the same store. A person
          following an account is usually looking at the CRM when something
          happens on it, and a notification they can only see by switching
          products is one they see late.
        */}
        <span className="ml-auto">
          <NotificationBell />
        </span>
      </div>

      <div className="px-3 pb-3">
        <button
          type="button"
          onClick={search.open}
          className={cn(
            "flex w-full items-center gap-2 rounded-md border border-line bg-surface px-2 py-1.5",
            "text-left text-xs text-faint hover:border-brand-300 hover:text-muted",
          )}
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
          <span className="flex-1">Search Pulse</span>
          <kbd className="rounded border border-line px-1 text-[10px]">⌘K</kbd>
        </button>
      </div>

      {/*
        Above the lists, where the Helpdesk and Sales Calls both keep theirs.
        It answers "where does my book stand" -- which is the question you ask
        before choosing which accounts to open, not after.
      */}
      <ul className="mb-3 px-2">
        <CrmLink to="/crm/dashboard" label="Companies dashboard" />
      </ul>

      {/*
        Both of these are the same route, so the path alone cannot say which
        one you are on -- see `CrmLink` for why `Companies` has to disown the
        unowned slice by hand.
      */}
      <ul className="px-2">
        <CrmLink
          to="/crm/companies"
          label="Companies"
          end
          match={(search) => search.get("unowned") !== "true"}
        />
        <CrmLink to="/crm/companies?unowned=true" label="Unmanaged accounts" />
      </ul>

      <div className="mt-4 px-4 pb-1 text-[11px] font-medium uppercase tracking-wide text-faint">
        To file
      </div>
      <ul className="px-2">
        <CrmLink
          to="/crm/duplicates"
          label="Possible duplicates"
          count={clusters.length}
          /* The one count worth pulling the eye: a duplicate left alone
             quietly accumulates a second history. */
          highlight={clusters.length > 0}
        />
        {/*
          "Not linked" sat here: a queue of accounts with no Team id, each
          with a box to type one into. It went because linking is done from
          Team's side now -- Team pushes the pair when it creates or matches
          an account -- so a queue in Pulse was a second place to do the job,
          and the one nobody was meant to use. A single company still shows
          "Not linked to Team" on its own page, which is where the fact is
          useful.
        */}
      </ul>
    </nav>
  );
}

/**
 * One nav row, highlighted only when you are actually on it.
 *
 * `NavLink` decides `isActive` from the path and nothing else, and two of these
 * links are the same path with a different query -- so on the plain list both
 * "Companies" and "Unmanaged accounts" lit up at once, which is a sidebar that
 * cannot answer the one question it is for.
 *
 * So the query is checked here too: a link that names parameters is only active
 * when the URL carries all of them, and one that does not can pass `match` to
 * say which slices belong to a sibling rather than to it. Deliberately only the
 * parameters the link names -- a filter or a sort somebody has added must not
 * unlight the row they are standing on.
 */
function CrmLink({
  to,
  label,
  count,
  highlight,
  end,
  match,
}: {
  to: string;
  label: string;
  count?: number;
  highlight?: boolean;
  end?: boolean;
  /** Extra condition on the current query, for links that share a path. */
  match?: (search: URLSearchParams) => boolean;
}) {
  const location = useLocation();
  const current = new URLSearchParams(location.search);
  const wanted = new URLSearchParams(to.split("?")[1] ?? "");
  const searchMatches =
    [...wanted].every(([key, value]) => current.get(key) === value) &&
    (match?.(current) ?? true);

  return (
    <li>
      <NavLink
        to={to}
        end={end}
        /* NavLink would announce this as the current page off the path alone,
           for the same reason it would highlight it. */
        aria-current={searchMatches ? "page" : "false"}
        className={({ isActive }) =>
          cn(
            "flex items-center gap-2 rounded-md px-2 py-1.5 text-sm",
            isActive && searchMatches
              ? "bg-surface-2 font-medium text-content"
              : "text-muted hover:bg-surface-2 hover:text-brand-600 dark:hover:text-brand-400",
          )
        }
      >
        <span className="min-w-0 flex-1 truncate">{label}</span>
        {count !== undefined && count > 0 ? (
          <span
            className={cn(
              "shrink-0 tabular-nums",
              highlight
                ? "rounded-full bg-brand-600 px-1.5 text-[10px] font-medium text-white"
                : "text-[11px] text-faint",
            )}
          >
            {count}
          </span>
        ) : null}
      </NavLink>
    </li>
  );
}
